(() => {
  'use strict';

  if (window.DirektProductHelp) {
    window.DirektProductHelp.init(document);
    return;
  }

  const STATE_KEY = 'direktProductHelpState';
  const ASSIST_KEY = 'direktAssistId';
  const DISMISS_KEY = 'direktHelpDismissals';
  const SEVEN_DAYS = 7 * 24 * 60 * 60 * 1000;
  const ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
  const memory = new Map();

  const safeJSON = (value, fallback) => {
    try { return value ? JSON.parse(value) : fallback; } catch (_) { return fallback; }
  };
  const storageGet = (kind, key) => {
    try { return window[kind].getItem(key); } catch (_) { return memory.get(`${kind}:${key}`) || null; }
  };
  const storageSet = (kind, key, value) => {
    try { window[kind].setItem(key, value); } catch (_) { memory.set(`${kind}:${key}`, value); }
  };
  const defaultState = () => ({
    signals: [], viewedProducts: [], categories: [], variantChanges: 0,
    buyBoxSeen: false, addedToCart: false, helpOpened: false,
    nudgeSeen: false, dismissed: false
  });
  const readState = () => ({ ...defaultState(), ...safeJSON(storageGet('sessionStorage', STATE_KEY), {}) });
  const writeState = (state) => storageSet('sessionStorage', STATE_KEY, JSON.stringify(state));

  const makeAssistId = () => {
    const existing = storageGet('sessionStorage', ASSIST_KEY);
    if (existing) return existing;
    const bytes = crypto.getRandomValues(new Uint8Array(6));
    const id = `DH-${Array.from(bytes, (byte) => ALPHABET[byte % ALPHABET.length]).join('')}`;
    storageSet('sessionStorage', ASSIST_KEY, id);
    return id;
  };

  const publishEvent = (name, data = {}) => {
    try { window.Shopify?.analytics?.publish?.(`direkt_help:${name}`, data); }
    catch (error) { console.warn('Direkt help analytics publish failed', error); }
  };

  const categoryLines = {
    door_gearbox: ['the complete gearbox, faceplate and centre case', 'stamps or logos plus backset and PZ measurements'],
    door_handle: ['both sides of the handle and its fitted context', 'PZ and screw-centre measurements'],
    window_mechanism: ['the full strip, centre gearbox and locking points', 'backset and overall-length measurements'],
    hinge: ['the open fitted hinge and fixing points', 'stack height and hinge length'],
    euro_cylinder: ['A/B measurements from the centre fixing screw', 'finish and thumbturn requirement (with no keys or address visible)'],
    patio_hardware: ['a wide fitted view plus front, back and side details', 'fixings, markings and measurements'],
    universal: ['the complete part and how it is fitted', 'markings plus a tape-measure view']
  };

  const isDesktop = () => window.matchMedia('(min-width: 769px)').matches;
  const visible = (element) => {
    if (!element) return false;
    const style = getComputedStyle(element);
    const rect = element.getBoundingClientRect();
    return style.display !== 'none' && style.visibility !== 'hidden' && rect.width > 0 && rect.height > 0;
  };

  const initModule = (module) => {
    if (!(module instanceof HTMLElement) || module.dataset.helpInitialized === 'true') return;
    if (module.closest('quick-add-modal, [data-product-quickshop]')) return;
    module.dataset.helpInitialized = 'true';

    const details = module.closest('.product__info-container, product-info, section') || document;
    const form = module.closest('form[action*="/cart/add"]') || details.querySelector('form[action*="/cart/add"]');
    const variantInput = form?.querySelector('[name="id"]');
    const variants = safeJSON(module.querySelector('[data-help-variants]')?.textContent, []);
    const dialog = module.querySelector('[data-help-dialog]');
    const nudge = module.querySelector('[data-help-nudge]');
    const copyStatus = module.querySelector('[data-help-copy-status]');
    let selectedVariant = null;
    let lastInvoker = null;
    let state = readState();

    const resolveVariant = () => {
      const id = variantInput?.value || module.dataset.variantId;
      selectedVariant = variants.find((variant) => String(variant.id) === String(id)) || selectedVariant || {
        id,
        title: module.dataset.variantTitle,
        sku: module.dataset.sku
      };
      if (String(selectedVariant.id) !== String(id)) {
        selectedVariant = { id, title: module.dataset.variantTitle, sku: module.dataset.sku };
      }
      return selectedVariant;
    };

    const config = () => {
      const variant = resolveVariant();
      const productUrl = new URL(module.dataset.productUrl || window.location.href, window.location.origin);
      productUrl.search = '';
      productUrl.hash = '';
      if (variant.id && variant.id !== 'not-selected') productUrl.searchParams.set('variant', variant.id);
      return {
        product_id: module.dataset.productId,
        product_title: module.dataset.productTitle,
        product_url: productUrl.toString(),
        variant_id: variant.id || '',
        variant_title: variant.title === 'Default Title' ? 'Default' : (variant.title || 'Not selected'),
        sku: variant.sku || 'No SKU shown',
        category: module.dataset.helpCategory || 'universal',
        ambiguity: module.dataset.ambiguity || 'standard',
        placement: 'below_product_form'
      };
    };

    const buildMessage = (assistId) => {
      const current = config();
      const lines = categoryLines[current.category] || categoryLines.universal;
      return [
        `Hi Direkt Hardware — enquiry ${assistId}.`, '',
        `I'm looking at: ${current.product_title}`,
        `Variant/SKU: ${current.variant_title} / ${current.sku}`,
        `Link: ${current.product_url}`, '',
        "I'm not sure whether it will fit my existing part. I will attach:",
        `• ${lines[0]}`, `• ${lines[1]}`, '',
        'What is broken or being replaced: [please add]'
      ].join('\n');
    };

    const setSignal = (signal) => {
      state = readState();
      if (!state.signals.includes(signal)) state.signals.push(signal);
      writeState(state);
      maybeShowNudge();
    };

    const crossSessionAllowed = () => module.dataset.crossSessionCap === 'true' && window.DIREKT_HELP_CONSENT === true;
    const recentlyDismissed = () => {
      if (!crossSessionAllowed()) return false;
      const data = safeJSON(storageGet('localStorage', DISMISS_KEY), { count: 0, last: 0 });
      return data.count >= 2 && Date.now() - data.last < SEVEN_DAYS;
    };
    const rememberDismissal = () => {
      if (!crossSessionAllowed()) return;
      const old = safeJSON(storageGet('localStorage', DISMISS_KEY), { count: 0, last: 0 });
      const inWindow = Date.now() - old.last < SEVEN_DAYS;
      storageSet('localStorage', DISMISS_KEY, JSON.stringify({ count: inWindow ? Math.min(old.count + 1, 2) : 1, last: Date.now() }));
    };

    const supportUIOpen = () => {
      const selectors = [
        'cart-drawer.active',
        'cart-notification.active',
        '[class*="cookie"][style*="position: fixed"]',
        '.whatsapp-floating-button',
        '[class*="chat-widget"]',
        'iframe[src*="whatsapp"]'
      ];
      return selectors.some((selector) => Array.from(document.querySelectorAll(selector)).some(visible));
    };
    const nudgeSuppressed = () => {
      state = readState();
      return module.dataset.nudgeEnabled !== 'true' || !isDesktop() || config().ambiguity !== 'high' ||
        state.addedToCart || state.helpOpened || state.dismissed || state.nudgeSeen || recentlyDismissed() || supportUIOpen();
    };
    function maybeShowNudge() {
      state = readState();
      if (!nudge || nudgeSuppressed() || state.signals.length < 2) return;
      nudge.hidden = false;
      state.nudgeSeen = true;
      writeState(state);
      publishEvent('nudge_impression', { ...config(), score: state.signals.length, contributing_signals: state.signals.slice() });
    }

    const current = config();
    const currentProduct = String(current.product_id || location.pathname);
    if (state.categories.includes(current.category) && !state.viewedProducts.includes(currentProduct)) setSignal('same_category_comparison');
    state = readState();
    if (!state.viewedProducts.includes(currentProduct)) state.viewedProducts.push(currentProduct);
    if (!state.categories.includes(current.category)) state.categories.push(current.category);
    writeState(state);

    const openDialog = (invoker, source) => {
      lastInvoker = invoker;
      state = readState();
      state.helpOpened = true;
      writeState(state);
      if (nudge) nudge.hidden = true;
      publishEvent('open', { ...config(), source, score: state.signals.length });
      if (typeof dialog?.showModal === 'function') dialog.showModal();
      else if (dialog) {
        dialog.setAttribute('open', '');
        dialog.setAttribute('data-fallback-open', '');
        dialog.querySelector('[data-help-close]')?.focus();
      }
    };
    const closeDialog = () => {
      if (typeof dialog?.close === 'function' && dialog.open) dialog.close();
      else if (dialog) {
        dialog.removeAttribute('open');
        dialog.removeAttribute('data-fallback-open');
        lastInvoker?.focus();
      }
    };

    module.querySelectorAll('[data-help-open]').forEach((button) => button.addEventListener('click', () => openDialog(button, nudge?.contains(button) ? 'nudge' : 'inline')));
    module.querySelectorAll('[data-help-close]').forEach((button) => button.addEventListener('click', closeDialog));
    dialog?.addEventListener('close', () => lastInvoker?.focus());
    dialog?.addEventListener('click', (event) => { if (event.target === dialog) closeDialog(); });
    dialog?.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      closeDialog();
    });

    module.querySelectorAll('[data-help-whatsapp]').forEach((link) => link.addEventListener('click', () => {
      const assistId = makeAssistId();
      const phone = (module.dataset.whatsappNumber || '').replace(/\D/g, '');
      link.href = `https://wa.me/${phone}?text=${encodeURIComponent(buildMessage(assistId))}`;
      state = readState();
      state.helpOpened = true;
      writeState(state);
      publishEvent('whatsapp_click', { ...config(), assist_id: assistId, source: link.dataset.helpSource || 'inline', score: state.signals.length });
    }));

    module.querySelector('[data-help-copy]')?.addEventListener('click', async () => {
      const assistId = makeAssistId();
      const message = buildMessage(assistId);
      let copied = false;
      try { await navigator.clipboard.writeText(message); copied = true; }
      catch (_) {
        const text = document.createElement('textarea');
        text.value = message;
        text.setAttribute('readonly', '');
        text.style.position = 'fixed'; text.style.opacity = '0';
        document.body.appendChild(text); text.select();
        try { copied = document.execCommand('copy'); } catch (_) { copied = false; }
        text.remove();
      }
      if (copyStatus) copyStatus.textContent = copied ? 'Message copied. Paste it into WhatsApp.' : 'Copy failed. Please use the WhatsApp link or phone number.';
      publishEvent('whatsapp_click', { ...config(), assist_id: assistId, source: 'copy_fallback', score: readState().signals.length });
    });

    module.querySelector('[data-help-finder]')?.addEventListener('click', () => publishEvent('finder_click', { ...config(), finder_type: 'product_metafield' }));
    module.querySelector('[data-help-dismiss]')?.addEventListener('click', () => {
      state = readState(); state.dismissed = true; writeState(state); rememberDismissal();
      if (nudge) nudge.hidden = true;
      publishEvent('dismiss', { ...config(), score: state.signals.length, placement: 'desktop_nudge' });
    });

    const onVariantChanged = () => {
      window.setTimeout(() => {
        resolveVariant();
        state = readState(); state.variantChanges += 1; writeState(state);
        if (state.variantChanges >= 2) setSignal('variant_changes');
      }, 0);
    };
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
      subscribe(PUB_SUB_EVENTS.variantChange, onVariantChanged);
    } else {
      details.addEventListener?.('change', onVariantChanged);
    }

    const onAddedToCart = () => {
      state = readState(); state.addedToCart = true; writeState(state);
      if (nudge) nudge.hidden = true;
      if (state.helpOpened) publishEvent('add_to_cart_after_help', { ...config(), assist_id: storageGet('sessionStorage', ASSIST_KEY) || undefined });
    };
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, onAddedToCart);
    } else {
      form?.addEventListener('submit', onAddedToCart);
    }

    if ('IntersectionObserver' in window) {
      const viewObserver = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= .5)) {
          publishEvent('view', { ...config(), device: isDesktop() ? 'desktop' : 'mobile' });
          viewObserver.disconnect();
        }
      }, { threshold: [.5] });
      viewObserver.observe(module);

      const buyBoxObserver = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          state = readState(); state.buyBoxSeen = true; writeState(state); buyBoxObserver.disconnect();
        }
      }, { threshold: [.25] });
      buyBoxObserver.observe(module);
    }

    window.setTimeout(() => {
      state = readState();
      if (state.buyBoxSeen && !state.addedToCart) setSignal('dwell_buybox_no_atc');
    }, 60000);

    publishEvent('eligible', config());
    maybeShowNudge();
  };

  const init = (root = document) => {
    if (root.matches?.('[data-product-help]')) initModule(root);
    root.querySelectorAll?.('[data-product-help]').forEach(initModule);
  };

  window.DirektProductHelp = { init };
  init(document);
  document.addEventListener('shopify:section:load', (event) => init(event.target));

  if (document.body && 'MutationObserver' in window) {
    new MutationObserver((mutations) => mutations.forEach((mutation) => mutation.addedNodes.forEach((node) => {
      if (node.nodeType === 1) init(node);
    }))).observe(document.body, { childList: true, subtree: true });
  }
})();
